// frontend/src/api.js
import axios from "axios";
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000',
  withCredentials: true,
});
// Global unauthorized handler registration (set from UserContext)
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = typeof fn === 'function' ? fn : null;
}

let csrfToken = null;
async function refreshCsrf() {
  const { data } = await api.get("/auth/csrf");
  csrfToken = data.token;
}

api.interceptors.request.use(async (cfg) => {
  const m = (cfg.method || "get").toUpperCase();
  if (["POST","PUT","PATCH","DELETE"].includes(m)) {
    if (!csrfToken) await refreshCsrf();
    cfg.headers = cfg.headers || {};
    if (typeof cfg.headers.set === 'function') {
      cfg.headers.set('x-csrf-token', csrfToken);
    } else {
      cfg.headers["x-csrf-token"] = csrfToken;
    }
  }
  // (dein bestehender Authorization-Header bleibt)
  const t = localStorage.getItem("token");
  if (t) {
    cfg.headers = cfg.headers || {};
    if (typeof cfg.headers.set === 'function') {
      cfg.headers.set('Authorization', `Bearer ${t}`);
    } else {
      cfg.headers.Authorization = `Bearer ${t}`;
    }
  }
  return cfg;
});

api.interceptors.response.use(undefined, async (err) => {
  const status = err?.response?.status;
  if (status === 401) {
    try { localStorage.removeItem('token'); } catch {}
    if (onUnauthorized) {
      try { await onUnauthorized(err); } catch {}
    } else {
      // Fallback: dispatch browser event so app can react
      try { window.dispatchEvent(new CustomEvent('apn:unauthorized')); } catch {}
    }
  }
  if (err?.response?.status === 403 && !err.config._retry) {
    await refreshCsrf();
    err.config._retry = true;
    err.config.headers = err.config.headers || {};
    if (typeof err.config.headers.set === 'function') {
      err.config.headers.set('x-csrf-token', csrfToken);
    } else {
      err.config.headers["x-csrf-token"] = csrfToken;
    }
    return api(err.config);
  }
  throw err;
});

export default api;

// Impressum API helpers
export const ImpressumAPI = {
  get: () => api.get('/impressum/settings').then(r => r.data),
  save: (payload) => api.put('/impressum/settings', payload).then(r => r.data),
  getPublic: (userId) => api.get(`/impressum/public/${userId}`).then(r => r.data),
};

// Services API helpers
export const ServicesAPI = {
  list: () => api.get('/services').then(r => r.data),
  get: (id) => api.get(`/services/${id}`).then(r => r.data),
  create: (payload) => api.post('/services', payload).then(r => r.data),
  update: (id, payload) => api.put(`/services/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/services/${id}`).then(r => r.data),
};

// Packages API helpers
export const PackagesAPI = {
  list: () => api.get('/packages').then(r => r.data),
  get: (id) => api.get(`/packages/${id}`).then(r => r.data),
  create: (payload) => api.post('/packages', payload).then(r => r.data),
  update: (id, payload) => api.put(`/packages/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/packages/${id}`).then(r => r.data),
  duplicate: (id) => api.post(`/packages/${id}/duplicate`).then(r => r.data),
  // assignments
  listServices: (id) => api.get(`/packages/${id}/services`).then(r => r.data),
  addService: (id, payload) => api.post(`/packages/${id}/services`, payload).then(r => r.data),
  updateService: (id, serviceId, payload) => api.put(`/packages/${id}/services/${serviceId}`, payload).then(r => r.data),
  removeService: (id, serviceId) => api.delete(`/packages/${id}/services/${serviceId}`).then(r => r.data),
};

export const DiscountsAPI = {
  async list() {
    const res = await api.get('/discounts');
    return res.data;
  },
  async create(payload) {
    const res = await api.post('/discounts', payload);
    return res.data;
  },
  async update(id, payload) {
    const res = await api.put(`/discounts/${id}`, payload);
    return res.data;
  },
  async remove(id) {
    const res = await api.delete(`/discounts/${id}`);
    return res.data;
  }
};

// Public Catalog (no auth)
export const CatalogAPI = {
  get: () => api.get('/api/catalog').then(r => r.data),
};

// Public Discounts (no auth)
export const PublicDiscountsAPI = {
  active: () => api.get('/api/discounts/active').then(r => r.data),
};

// Public Events (no auth)
export const PublicEventsAPI = {
  list: (params = {}) => api.get('/api/events', { params }).then(r => r.data),
};

// Game Servers API helpers
export const ServersAPI = {
  list: (params = {}) => api.get('/servers', { params }).then(r => r.data),
  get: (id) => api.get(`/servers/${id}`).then(r => r.data),
  status: (id) => api.get(`/servers/${id}/status`).then(r => r.data),
  create: (payload) => api.post('/servers', payload).then(r => r.data),
  adminCreate: (payload) => api.post('/servers/admin/create', payload).then(r => r.data),
  update: (id, payload) => api.patch(`/servers/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/servers/${id}`).then(r => r.data),
};

// Events API helpers
export const EventsAPI = {
  list: (params = {}) => api.get('/events', { params }).then(r => r.data),
  myUpcoming: (params = {}) => api.get('/events/my/upcoming', { params }).then(r => r.data),
  get: (id) => api.get(`/events/${id}`).then(r => r.data),
  create: (payload) => api.post('/events', payload).then(r => r.data),
  update: (id, payload) => api.patch(`/events/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/events/${id}`).then(r => r.data),
  attendees: (id) => api.get(`/events/${id}/attendees`).then(r => r.data),
  register: (id, action) => api.post(`/events/${id}/register`, { action }).then(r => r.data),
  uploadPoster: async (id, file) => {
    const fd = new FormData();
    fd.append('poster', file);
    const { data } = await api.post(`/events/${id}/poster`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  invite: (id, payload) => api.post(`/events/${id}/invite`, payload).then(r => r.data),
  invitations: (id) => api.get(`/events/${id}/invitations`).then(r => r.data),
  broadcast: (id, payload) => api.post(`/events/${id}/broadcast`, payload).then(r => r.data),
  comments: (id) => api.get(`/events/${id}/comments`).then(r => r.data),
  addComment: (id, body) => api.post(`/events/${id}/comments`, { body }).then(r => r.data),
  deleteComment: (id, commentId) => api.delete(`/events/${id}/comments/${commentId}`).then(r => r.data),
  logs: (id) => api.get(`/events/${id}/logs`).then(r => r.data),
};

// Users API helpers
export const UsersAPI = {
  search: (q) => api.get('/users/search', { params: { q } }).then(r => r.data),
  publicMentors: () => api.get('/users/public/mentors').then(r => r.data),
};

// Notifications API
export const NotificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }).then(r => r.data),
  unreadCount: () => api.get('/notifications/unread-count').then(r => r.data),
  markRead: (id) => api.patch(`/notifications/${id}/read`).then(r => r.data),
  markAllRead: () => api.post('/notifications/mark-all-read').then(r => r.data),
};

// Event polls API
export const EventPollsAPI = {
  list: (eventId) => api.get(`/events/${eventId}/polls`).then(r => r.data),
  create: (eventId, payload) => api.post(`/events/${eventId}/polls`, payload).then(r => r.data),
  vote: (eventId, pollId, optionId) => api.post(`/events/${eventId}/polls/${pollId}/vote`, { optionId }).then(r => r.data),
  close: (eventId, pollId) => api.post(`/events/${eventId}/polls/${pollId}/close`).then(r => r.data),
  remove: (eventId, pollId) => api.delete(`/events/${eventId}/polls/${pollId}`).then(r => r.data),
};

// Maintenance API
export const MaintenanceAPI = {
  status: () => api.get('/maintenance/status').then(r => r.data),
  getSettings: () => api.get('/maintenance/settings').then(r => r.data),
  saveSettings: (payload) => api.put('/maintenance/settings', payload).then(r => r.data),
};

// Newsletter API
export const NewsletterAPI = {
  subscribe: (payload) => api.post('/newsletter/subscribe', payload).then(r => r.data),
  unsubscribe: (payload) => api.post('/newsletter/unsubscribe', payload).then(r => r.data),
};

// Newsletter Admin API
export const NewsletterAdminAPI = {
  list: (params = {}) => api.get('/newsletter/admin', { params }).then(r => r.data),
  exportCsv: async (params = {}) => {
    const { data } = await api.get('/newsletter/admin/export.csv', { params, responseType: 'blob' });
    return data; // Blob
  },
  stats: () => api.get('/newsletter/admin/stats').then(r => r.data),
  resendConfirmation: (email) => api.post('/newsletter/admin/resend-confirmation', { email }).then(r => r.data),
};

// Offers API
export const OffersAPI = {
  request: (payload) => api.post('/offers/request', payload).then(r => r.data),
};

// Feature Requests API
export const FeatureRequestsAPI = {
  list: (params = {}) => api.get('/feature-requests', { params }).then(r => r.data),
  get: (id) => api.get(`/feature-requests/${id}`).then(r => r.data),
  create: async ({ title, description, type, images }) => {
    const fd = new FormData();
    fd.append('title', title);
    if (description) fd.append('description', description);
    if (type) fd.append('type', type);
    (images || []).forEach(f => fd.append('images', f));
    const { data } = await api.post('/feature-requests', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  vote: (id, value) => api.post(`/feature-requests/${id}/vote`, { value }).then(r => r.data),
  comments: (id) => api.get(`/feature-requests/${id}/comments`).then(r => r.data),
  addComment: (id, body) => api.post(`/feature-requests/${id}/comments`, { body }).then(r => r.data),
  deleteComment: (id, commentId) => api.delete(`/feature-requests/${id}/comments/${commentId}`).then(r => r.data),
  updateComment: (id, commentId, body) => api.patch(`/feature-requests/${id}/comments/${commentId}`, { body }).then(r => r.data),
  changeStatus: (id, status, note) => api.patch(`/feature-requests/${id}/status`, { status, note }).then(r => r.data),
  resetRejected: (id, note) => api.post(`/feature-requests/${id}/status/reset-rejected`, { note }).then(r => r.data),
  reactComment: (frId, commentId, value) => api.post(`/feature-requests/${frId}/comments/${commentId}/react`, { value }).then(r => r.data),
  addStatusNote: (id, note) => api.post(`/feature-requests/${id}/status/note`, { note }).then(r => r.data),
};

